import { useCallback } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { PROMPT_MENU_TYPE } from 'src/components/RichSlate';
import { MAX_RESOURCE_PER_TYPE } from 'src/data/common';
import {
  generateResourceUploadId,
  getResourceContentErrorMessage,
  RESOURCE_CONTENT_TYPE,
  RESOURCE_STATUS,
} from 'src/data/performance';
import * as events from 'src/lib/events';

import { ACTION } from '../../const';
import { filterBrandBriefItems, getPromptMenuTypeChannel } from '../../utils';

const TIMEOUT = 1000;

export const useHandlers = ({
  imageItems,
  documentItems,
  setMenuType,
  onCloseMenu,
  isUploadingImage,
  isUploadingDocument,
  index,
  setSearchValue,
  brandBriefsMenuItems,
  setFilteredBankItems,
  resourcesTypeFilter,
  resourcesTagFilter,
  setResourcesTagFilter,
  setResourcesTypeFilter,
  searchValue,
  onConnectExternalChannel,
  onAction,
  shouldOpenConfirmPopup,
  uploadFileHelperRef,
}) => {
  const handleUploadFile = useCallback(
    async (resourceType) => {
      // Check if user can even upload any new files
      if (shouldOpenConfirmPopup([resourceType])) {
        onAction(ACTION.displayMessage, {
          message: `You can use a maximum of ${MAX_RESOURCE_PER_TYPE} ${resourceType}s`,
        });
        return;
      }

      try {
        // Request file selection from user
        const result = await uploadFileHelperRef.current.openFileDialog(resourceType);
        onCloseMenu(); // we need to keep menu opened until user finishes with "file" dialog, as we need that "hidden" `input` to be available
        if (!result) {
          return;
        }

        // If we had a single rejection - we should display the error and return
        const rejectedFileErrorCode = result.fileRejections?.[0]?.errors?.[0]?.code;
        if (rejectedFileErrorCode) {
          const errorMessage = getResourceContentErrorMessage(resourceType, rejectedFileErrorCode);
          onAction(ACTION.displayError, new Error(errorMessage));
          return;
        }

        const acceptedFile = result.acceptedFiles?.[0];
        if (!acceptedFile) {
          return;
        }

        // This ID is needed to track the file between several component layers
        const randomId = generateResourceUploadId(resourceType);

        // Parent component should handle the upload
        const resource = await onAction(ACTION.uploadResourceFile, {
          resourceType,
          file: acceptedFile,
          randomId,
          index,
        });

        if (resource?.status === RESOURCE_STATUS.active) {
          switch (resourceType) {
            case RESOURCE_CONTENT_TYPE.image:
              onAction(ACTION.insertImage, {
                item: resource,
                skipConfirmation: true,
                randomId,
                index,
              });
              break;
            case RESOURCE_CONTENT_TYPE.document:
              onAction(ACTION.insertDocument, {
                item: resource,
                skipConfirmation: true,
                randomId,
                index,
              });
              break;
            default:
              break;
          }
        }
      } catch (error) {
        onAction(ACTION.displayError, error);
      }
    },
    [onCloseMenu, shouldOpenConfirmPopup, onAction, uploadFileHelperRef, index]
  );

  const debouncedTrackResourceSearchSubmitted = useDebouncedCallback(
    ({ term, tagsFilters, typeFilters }) => {
      onAction(ACTION.trackEvent, {
        event: events.PROMPTS.promptsResourceSearchSubmitted,
        term,
        tagsFilters: tagsFilters.join(', '),
        typeFilters: typeFilters.join(', '),
      });
    },
    TIMEOUT
  );

  const handleSearchChange = useCallback(
    (value, type) => {
      if (type === PROMPT_MENU_TYPE.url) {
        setSearchValue(value.trim());
      } else {
        setSearchValue(value);

        debouncedTrackResourceSearchSubmitted({
          term: value,
          tagsFilters: resourcesTagFilter,
          typeFilters: resourcesTypeFilter,
        });

        const items = filterBrandBriefItems(value, brandBriefsMenuItems);
        setFilteredBankItems(items);
      }
    },
    [
      setSearchValue,
      brandBriefsMenuItems,
      setFilteredBankItems,
      debouncedTrackResourceSearchSubmitted,
      resourcesTagFilter,
      resourcesTypeFilter,
    ]
  );

  const handleMenuCardClick = useCallback(
    (event) => {
      const type = event.currentTarget?.dataset?.type;
      if (!type) {
        return;
      }

      if ([PROMPT_MENU_TYPE.salesforce, PROMPT_MENU_TYPE.microsoftDynamicsSales].includes(type)) {
        onConnectExternalChannel({
          channel: getPromptMenuTypeChannel(type),
          callback: () => setMenuType(type),
        });
        return;
      }

      setMenuType(type);

      // If we don't have existing images or documents - execute "upload" flow right away
      const hasImages = imageItems?.length > 0;
      const hasDocuments = documentItems?.length > 0;
      if (
        (type === PROMPT_MENU_TYPE.image && !hasImages && !isUploadingImage) ||
        (type === PROMPT_MENU_TYPE.document && !hasDocuments && !isUploadingDocument)
      ) {
        handleUploadFile(type);
      }
    },
    [
      imageItems?.length,
      documentItems?.length,
      isUploadingImage,
      handleUploadFile,
      setMenuType,
      isUploadingDocument,
      onConnectExternalChannel,
    ]
  );

  const handleChangeResourcesTagFilter = useCallback(
    (newValue) => {
      setResourcesTagFilter(newValue);

      onAction(ACTION.trackEvent, {
        event: events.PROMPTS.promptsResourceSearchSubmitted,
        term: searchValue,
        tagsFilters: newValue.join(', '),
        typeFilters: resourcesTypeFilter.join(', '),
      });
    },
    [setResourcesTagFilter, onAction, searchValue, resourcesTypeFilter]
  );

  const handleChangeResourcesTypeFilter = useCallback(
    (newValue) => {
      setResourcesTypeFilter(newValue);

      onAction(ACTION.trackEvent, {
        event: events.PROMPTS.promptsResourceSearchSubmitted,
        term: searchValue,
        tagsFilters: resourcesTagFilter.join(', '),
        typeFilters: newValue.join(', '),
      });
    },
    [setResourcesTypeFilter, onAction, searchValue, resourcesTagFilter]
  );

  return {
    handleUploadFile,
    handleSearchChange,
    handleMenuCardClick,
    handleChangeResourcesTagFilter,
    handleChangeResourcesTypeFilter,
  };
};
